import React from 'react';
import { Search, X } from 'lucide-react';
import { useGetCustomer } from '../../../integrations/react_query/queries';
import { EQuerykeys } from '../../../models';
import useDebounce from '../../../hooks/useDebounce';
import { useAttachCustomerToCart } from '../../../integrations/react_query/mutations';

interface CustomerSearchMobileProps {
	onCustomerSelect?: () => void;
}

export const CustomerSearchMobile = ({ onCustomerSelect }: CustomerSearchMobileProps) => {
	const [searchInput, setSearchInput] = React.useState('');
	const [isOpen, setIsOpen] = React.useState(false);
	const debouncedSearch = useDebounce(searchInput, 300);

	const { data: customerData } = useGetCustomer([EQuerykeys.customer]);
	const { mutateAsync: attachCustomer } = useAttachCustomerToCart();

	const filteredCustomers = React.useMemo(() => {
		if (!debouncedSearch || debouncedSearch.length < 1) return [];
		return (customerData ?? []).filter((customer: any) => {
			const fullName = `${customer?.firstName || ''} ${customer?.lastName || ''}`.toLowerCase();
			const phone = (customer?.phoneNumber || '').toLowerCase();
			return fullName.includes(debouncedSearch.toLowerCase()) || phone.includes(debouncedSearch.toLowerCase());
		});
	}, [debouncedSearch, customerData]);

	const handleSelect = async (customer: any) => {
		try {
			await attachCustomer({
				id: customer._id,
				firstName: customer.firstName,
				lastName: customer.lastName,
				email: customer.email,
				phoneNumber: customer.phoneNumber,
				birthday: customer.birthday,
				organizationName: customer.organizationName,
			});
			setSearchInput('');
			setIsOpen(false);
			onCustomerSelect?.();
		} catch (error) {
			console.error('Error attaching customer:', error);
		}
	};

	return (
		<div className="relative w-full">
			<div className="relative flex items-center">
				<Search size={18} className="absolute left-3 text-gray-400 pointer-events-none" />
				<input
					type="text"
					placeholder="Search customer by name or phone..."
					value={searchInput}
					onChange={(e) => {
						setSearchInput(e.target.value);
						setIsOpen(true);
					}}
					onFocus={() => setIsOpen(true)}
					className="w-full pl-10 pr-8 py-2.5 border-none rounded-lg focus:outline-none text-sm"
				/>
				{searchInput && (
					<button
						onClick={() => {
							setSearchInput('');
							setIsOpen(false);
						}}
						className="absolute right-3 text-gray-400 hover:text-gray-600"
					>
						<X size={16} />
					</button>
				)}
			</div>

			{isOpen && searchInput && (
				<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto">
					{filteredCustomers.length === 0 ? (
						<div className="p-3 text-center text-gray-500 text-sm">No customers found</div>
					) : (
						<ul className="divide-y divide-gray-200">
							{filteredCustomers.map((customer: any) => (
								<li key={customer._id}>
									<button
										onClick={() => handleSelect(customer)}
										className="w-full px-4 py-3 text-left hover:bg-blue-50 active:bg-blue-100 transition-colors"
									>
										<div className="flex flex-col gap-0.5">
											<span className="font-medium text-sm text-gray-900">
												{customer.firstName} {customer.lastName}
											</span>
											{customer.phoneNumber && <span className="text-xs text-gray-500">{customer.phoneNumber}</span>}
										</div>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
};

export default CustomerSearchMobile;
